import {
  SLOT_TYPE,
  FIELD_LABELS,
  SLOT_FIELDS,
  BOOKING_FIELD_KEYS,
} from '../../../../constants/BookingViewConstants';
import {
  SlotDetail,
  ContextualNode,
  Booking,
  TableData,
} from './BookingView.types';
import { getGtmStatusDisplay } from '../../../CreateBooking/constants/DetailedBookingConstants';

const calculateTotal = (slots: ContextualNode[], field: string): number =>
  slots.reduce((sum, slot) => sum + (Number(slot[field]) || 0), 0);

//Details to be displayed out of the table
const formatSlotsForDisplay = (slots: ContextualNode[]): SlotDetail[] =>
  slots.map((slot) => ({
    contextualNode: `[${slot[SLOT_FIELDS.SLOT_OR_PACKAGE_NAME]}] ${slot[SLOT_FIELDS.CONTEXTUAL_NODE] ?? '-'
      }`,
    impressionsBooked: slot[SLOT_FIELDS.IMPRESSIONS_BOOKED] ?? '-',
    // Allocated/waitlisted are only populated for waitlist bookings;
    // fall back to '-' for non-waitlist bookings that never carry these fields.
    impressionsAllocated: slot[SLOT_FIELDS.IMPRESSIONS_ALLOCATED] ?? '-',
    impressionsWaitlisted: slot[SLOT_FIELDS.IMPRESSIONS_WAITLISTED] ?? '-',
    valueBlocked: slot[SLOT_FIELDS.VALUE_BLOCKED] ?? '-',
    sovPercentage: slot[SLOT_FIELDS.PERCENTAGE_BOOKED] ?? '-',
  }));

export const transformBookingWithSlots = (
  booking: Booking,
  t: (key: string) => string
) => {
  const {
    contextualNodes = [],
    entryId,
    brandName,
    accountExecName,
    categoryName,
    accountManagerName,
    gtmStatus,
    typeOfPurchase,
    sfOpportunityId,
    commentsByPOC,
  } = booking;

  const hasNodes = Array.isArray(contextualNodes) && contextualNodes.length > 0;

  const slotOrPackageName = hasNodes
    ? [...new Set(contextualNodes.map((n) => n[SLOT_FIELDS.SLOT_OR_PACKAGE_NAME]).filter(Boolean))].join(', ') || '-'
    : '-';

  const baseResult: TableData = {
    [BOOKING_FIELD_KEYS.ENTRY_ID]: entryId,
    [BOOKING_FIELD_KEYS.BRAND_NAME]: brandName,
    [BOOKING_FIELD_KEYS.ACCOUNT_EXEC_NAME]: accountExecName,
    [BOOKING_FIELD_KEYS.ACCOUNT_MANAGER_NAME]: accountManagerName,
    [BOOKING_FIELD_KEYS.CATEGORY_NAME]: categoryName,
    [BOOKING_FIELD_KEYS.GTM_STATUS]: getGtmStatusDisplay(gtmStatus),
    [BOOKING_FIELD_KEYS.TYPE_OF_PURCHASE]: typeOfPurchase,
    [BOOKING_FIELD_KEYS.SF_OPPORTUNITY_ID]: sfOpportunityId,
    [BOOKING_FIELD_KEYS.GP_SLOT]: slotOrPackageName,
    ...(commentsByPOC && {
      [BOOKING_FIELD_KEYS.COMMENTS_BY_POC]: commentsByPOC,
    }),
  };

  if (!hasNodes) {
    return {
      tableData: {
        ...baseResult,
        [FIELD_LABELS.NON_CONTEXTUAL.NODE]: '-',
        [FIELD_LABELS.CONTEXTUAL.NODE]: '-',
      },
      contextualSlotDetails: [],
      nonContextualSlotDetails: [],
    };
  }

  const splitSlots = (slots: typeof contextualNodes) => ({
    contextual: slots.filter(
      (slot) => slot[SLOT_FIELDS.SLOT_OR_PACKAGE_NAME] !== SLOT_TYPE.GATEWAY
    ),
    nonContextual: slots.filter(
      (slot) => slot[SLOT_FIELDS.SLOT_OR_PACKAGE_NAME] === SLOT_TYPE.GATEWAY
    ),
  });

  const { contextual, nonContextual } = splitSlots(contextualNodes);

  const buildSlotData = (slots: typeof contextualNodes) =>
    slots.length
      ? {
        impressions: calculateTotal(slots, SLOT_FIELDS.IMPRESSIONS_BOOKED),
        value: calculateTotal(slots, SLOT_FIELDS.VALUE_BLOCKED),
        details: formatSlotsForDisplay(slots),
      }
      : null;

  const contextualData = buildSlotData(contextual);

  const nonContextualData = buildSlotData(nonContextual);

  const tableData: TableData = {
    ...baseResult,
    ...(contextualData && {
      [BOOKING_FIELD_KEYS.TOTAL_IMPRESSIONS_BOOKED]: contextualData.impressions,
      [BOOKING_FIELD_KEYS.TOTAL_VALUE_BLOCKED]: contextualData.value,
    }),
    ...(nonContextualData && {
      [BOOKING_FIELD_KEYS.TOTAL_IMPRESSIONS_BOOKED_NON_CONTEXTUAL]:
        nonContextualData.impressions,
      [BOOKING_FIELD_KEYS.TOTAL_VALUE_BLOCKED_NON_CONTEXTUAL]:
        nonContextualData.value,
    }),
  };

  return {
    tableData,
    contextualSlotDetails: contextualData?.details ?? [],
    nonContextualSlotDetails: nonContextualData?.details ?? [],
  };
};
